// features/item-filter/useGridChartScope.js
//
// Single owner of "what's in scope" for a page that pairs a grid with a
// chart. Encapsulates two layers:
//
//   1. Filter scope — item search (useItemFilter), location, product
//      category, and "Only items with active or scheduled promotions" on the
//      page that offers it. Drives BOTH the grid's data fetcher AND the
//      chart's, so the chart always aggregates the same population the grid
//      shows.
//   2. Drill-down — when the merchant clicks a single grid row, the chart
//      zooms to that row. The grid keeps showing the filtered population
//      (drill-down does not narrow the grid). Clicking the same row again
//      clears the drill-down, and so does any filter change: the new
//      population may not hold the drilled row, and the chart must not keep
//      showing a row the grid has dropped.
//
// Precedence rule (encoded once, here): when drill-down is active, the
// chart fetches by item+location only and ignores the filter scope — the
// drilled row was picked under the current filters, so they would add
// nothing.
//
// Returned shape — see useGridChartScope.test.jsx for the contract.

import { useCallback, useMemo } from 'react';
import { useTabPersistedState } from '../../tabs/useTabPersistedState';
import { itemLocationRowId } from '../../utils/itemLocationRowId';
import { useItemFilter } from './useItemFilter';

export function useGridChartScope({ beforeChange } = {}) {
  // Every scope slot persists per tab — survives F5 and supports the
  // side-by-side use case (two Inventory tabs with different locations).
  const [drillDown, setDrillDown] = useTabPersistedState('drillDown', null);
  const clearDrillDown = useCallback(() => setDrillDown(null), [setDrillDown]);

  const filter = useItemFilter({ beforeChange, onChange: clearDrillDown });
  const [selectedLocation, storeLocation] = useTabPersistedState('selectedLocation', 'all');
  // selectedProductCategory holds the FULL ProductCategory object (or null
  // for "no filter") so the picker's value prop wires straight through.
  const [selectedProductCategory, storeProductCategory] = useTabPersistedState('selectedProductCategory', null);
  const setSelectedLocation = useCallback((value) => {
    storeLocation(value);
    clearDrillDown();
  }, [storeLocation, clearDrillDown]);
  const setSelectedProductCategory = useCallback((value) => {
    storeProductCategory(value);
    clearDrillDown();
  }, [storeProductCategory, clearDrillDown]);
  const [promotedOnly, storePromotedOnly] = useTabPersistedState('promotedOnly', false);
  const setPromotedOnly = useCallback((value) => {
    storePromotedOnly(value);
    clearDrillDown();
  }, [storePromotedOnly, clearDrillDown]);

  // Toggle behaviour: clicking the same row twice clears the drill-down.
  // Identity uses (itemCode, locationCode) since rows are item-location
  // pairs across both pages. The scope object also carries itemName and sku,
  // which are what DrillDownChip NAMES the row with — see
  // drillDownFromCellClick for why the code alone is not enough.
  const handleRowSelect = useCallback((item) => {
    setDrillDown((prev) => {
      if (!item) return null;
      if (prev && prev.itemCode === item.itemCode && prev.locationCode === item.locationCode) {
        return null;
      }
      return item;
    });
  }, [setDrillDown]);

  // Row identity for the grids' `*-row-active` highlight — composed by the
  // one helper the grids' own row ids come from, so the two cannot drift.
  const drillDownRowId = drillDown
    ? itemLocationRowId(drillDown.itemCode, drillDown.locationCode)
    : null;

  // Chart fetch params. Drill-down wins (single-row view); otherwise
  // mirror the filter scope so the chart aggregates the same population
  // the grid renders.
  //
  // Scope slots, in the app's own vocabulary — uniformly camelCase. The
  // translation to the backend's snake_case query string happens in exactly
  // one place, `scopeParams` (./scopeParams.js); this
  // hook must not smuggle wire names through, because a key that looks like
  // it is already wire-shaped invites a consumer to forward it blind — which
  // is how `product_category_id` came to be dropped silently while the grid
  // beside the chart kept filtering correctly.
  //   - `productCategoryId` — ProductCategory FK GID.
  //   - `locationCode` — LocationCode string (matches the grids).
  //   - `search` or `itemCode` — the item filter (`filterScope`): typed text,
  //     or one picked item.
  //   - `hasPromotions` — 'true' while "Only items with active or scheduled
  //     promotions" is on.
  //   - `itemCode` + `locationCode` — a drilled-down row.
  const chartExtraParams = useMemo(() => {
    if (drillDown) {
      return {
        itemCode: drillDown.itemCode,
        locationCode: drillDown.locationCode,
      };
    }
    return {
      locationCode: selectedLocation === 'all' ? '' : selectedLocation,
      productCategoryId: selectedProductCategory?.id || '',
      hasPromotions: promotedOnly ? 'true' : '',
      ...filter.scope,
    };
  }, [drillDown, selectedLocation, selectedProductCategory, promotedOnly, filter.scope]);

  return {
    // For ItemFilterBar + data fetchers (filter.params are the query params)
    filter,
    selectedLocation, setSelectedLocation,
    selectedProductCategory, setSelectedProductCategory,
    promotedOnly, setPromotedOnly,
    // For grid (drives row highlight + drill-down handler)
    drillDown,
    drillDownRowId,
    handleRowSelect,
    clearDrillDown,
    // For chart
    chartExtraParams,
  };
}
